/**
 * Named Report Templates
 *
 * Pre-defined report types that frame MMM data for specific audiences and
 * use cases. Each template controls which sections to include, the Thai
 * title/description, and the Gemini system prompt modifier for the AI summary.
 *
 * Used by:
 * - ReportLibrary.tsx (template badges + filtering)
 * - mmmReportService.ts (section selection + AI prompt)
 */

// ============================================================================
// TYPES
// ============================================================================

export type ReportMode = 'performance' | 'mmm' | 'both'

export interface ReportTemplate {
  /** Unique key */
  key: string
  /** Which chat mode this template is offered in (Performance Advisor, MMM Advisor, or both) */
  mode: ReportMode
  /** Thai display name */
  labelTh: string
  /**
   * Translation KEY for the one-line subtitle under labelTh.
   *
   * A key, not a sentence: this table is module scope. It renders in the chat's
   * report-type picker and in the generated report's meta line — so a Thai
   * reader was getting a Thai label with an English subtitle beneath it.
   */
  descriptionKey: string
  /** Icon name (lucide-react) */
  icon: string
  /** Which sections to include in the HTML report */
  sections: ReportSection[]
  /** Short Gemini prompt suffix (legacy — kept for backwards compat with mmmReportService) */
  aiPromptSuffix: string
  /**
   * When true, the template is excluded from the picker UI but stays in the
   * registry (so generatePerformanceReport can still resolve it if requested
   * directly by key). Use for templates that aren't ready for users yet
   * (e.g. missing benchmark data) without deleting them.
   */
  hidden?: boolean
  /**
   * Full structural outline the AI must follow for THIS template.
   * Replaces the one-line aiPromptSuffix as the primary instruction set
   * so every report type produces a visibly different narrative — not just
   * a different title with the same body. (Fix: report templates were
   * collapsing to a single Campaign Performance report.)
   */
  aiPromptStructure: string
  /** Badge color classes for UI */
  badgeColor: string
}

export type ReportSection =
  | 'executive_summary'
  | 'stage_breakdown'
  | 'budget_reallocation'
  | 'channel_performance'
  | 'funnel_analysis'
  | 'roi_comparison'
  | 'confidence_breakdown'
  | 'synergy_map'
  | 'industry_benchmark'
  | 'proof_points'
  | 'methodology'

// ============================================================================
// REPORT TEMPLATES (7 named templates)
// ============================================================================

/**
 * 1. Executive Summary — one-page board-ready overview
 */
export const EXECUTIVE_SUMMARY_TEMPLATE: ReportTemplate = {
  key: 'executive_summary',
  mode: 'performance',
  labelTh: 'สรุปผู้บริหาร (Performance)',
  descriptionKey: 'reports.tplExecutiveSummary',
  icon: 'crown',
  sections: ['executive_summary', 'stage_breakdown', 'channel_performance', 'funnel_analysis'],
  aiPromptSuffix: 'เขียนสรุปแบบกระชับสำหรับผู้บริหาร — มุ่งเน้นผลลัพธ์และคำแนะนำหลัก ไม่เกิน 3 ย่อหน้า',
  // The headline used to require "Conversions vs forecast". No forecast exists in
  // this context, so that instruction could only be satisfied by inventing one.
  // Kept out of the prompt text itself: quoting a retired instruction puts it back
  // in front of the model.
  aiPromptStructure: `**Executive Summary Report** — เขียนแบบ board-ready (ไม่เกิน 3 ย่อหน้า, รวมไม่เกิน 250 คำ):

1. **The Headline** (1 ย่อหน้า, 3-4 บรรทัด): หนึ่งย่อหน้าเดียวที่ตอบคำถาม "ผลงานในช่วงนี้ดีหรือไม่ดี และทำไม" พร้อมตัวเลขหลัก 3 ตัว (ต้นทุนต่อ conversion, จำนวน conversion เทียบกับงวดก่อน, ROAS)
   — ระบบนี้ไม่มีเป้าหมายหรือค่าคาดการณ์ ห้ามเทียบผลงานกับตัวเลขเป้าใดๆ
   — ถ้าไม่มีช่องใดส่งมูลค่า conversion มา ให้ข้าม ROAS ไป อย่าเขียนว่า 0
2. **The Decision** (1 ย่อหน้า): คำแนะนำเดียวที่ผู้บริหารต้องตัดสินใจสัปดาห์นี้ — ระบุจำนวนเงิน + ช่องที่ย้าย/เพิ่ม/หยุด พร้อมเหตุผลตัวเลข
3. **The Risk** (1 ย่อหน้า): ความเสี่ยงสำคัญ 1-2 อย่างที่ต้องจับตา + ตัวเลขที่ trigger การทบทวน

ห้ามใส่ table ห้ามใส่ bullet list ยาว ๆ — ผู้บริหารอ่าน 30 วินาที`,
  badgeColor: 'bg-amber-100 text-amber-700',
}

/**
 * 2. Budget Reallocation Plan — detailed budget shift rationale
 */
export const BUDGET_REALLOCATION_TEMPLATE: ReportTemplate = {
  key: 'budget_reallocation',
  mode: 'mmm',
  labelTh: 'แผนปรับเปลี่ยนงบประมาณ',
  descriptionKey: 'reports.tplBudgetReallocation',
  icon: 'wallet',
  sections: ['budget_reallocation', 'channel_performance', 'roi_comparison', 'methodology'],
  aiPromptSuffix: 'อธิบายเหตุผลในการปรับเปลี่ยนงบประมาณในแต่ละช่องทาง — ทำไมจึงตัด ทำไมจึงเพิ่ม',
  aiPromptStructure: `**Budget Reallocation Plan** — โครงสร้างต้องเป็น "ตัด → เพิ่ม → เหตุผล → สิ่งที่ต้องเฝ้าดู"

อ่านจาก BUDGET ALLOCATION CHANGES เท่านั้น ทุกช่องมีเลข current → recommended (+/-%) อยู่แล้ว

1. **🛑 CUT (ช่องที่ถูกลดงบ)**: ตาราง HTML — Channel | Current Spend | New Spend | Δ
   เกณฑ์ที่ทำให้ถูกลดคือ conversions ต่อบาท ต่ำกว่าค่ากลางของกลุ่ม พูดตามนั้น
   ห้ามอ้าง CPL/ROAS/CTR รายช่อง หรือ pacing — ไม่มีอยู่ในข้อมูลนี้

2. **📈 SCALE (ช่องที่ถูกเพิ่มงบ)**: ตารางเดียวกัน + อธิบายว่าเงินที่เพิ่มมาจากช่องที่ถูกลด
   เป็นการย้ายงบก้อนเดิม ไม่ใช่งบใหม่ ห้ามใส่ % คาดการณ์ผลลัพธ์

3. **🎯 NET**: งบรวมเท่าเดิม ย้ายไปเท่าไร — และบอกตรง ๆ ว่าทำนายผลลัพธ์หลังย้ายไม่ได้
   เพราะไม่มีโมเดล สิ่งที่วัดได้คือ IMPACT TOTALS ปัจจุบัน (conversions, ต้นทุนต่อ conversion)

4. **👀 สิ่งที่ต้องเฝ้าดู**: 2-3 metric จาก IMPACT TOTALS ที่ควรดูหลังปรับ
   ห้ามตั้ง threshold rollback เป็นตัวเลข — ไม่มีฐานให้ตั้ง`,
  badgeColor: 'bg-blue-100 text-blue-700',
}

/**
 * 3. Channel Performance — per-channel deep dive
 */
export const CHANNEL_PERFORMANCE_TEMPLATE: ReportTemplate = {
  key: 'channel_performance',
  mode: 'performance',
  labelTh: 'ประสิทธิภาพช่องทาง',
  descriptionKey: 'reports.tplChannelPerformance',
  icon: 'bar-chart-3',
  sections: ['stage_breakdown', 'channel_performance', 'funnel_analysis'],
  aiPromptSuffix: 'วิเคราะห์ประสิทธิภาพของแต่ละช่องทาง — ช่องไหนทำงานได้ดี ช่องไหนต้องปรับ',
  aiPromptStructure: `**Channel Performance Deep Dive** — เขียนเป็น per-channel analysis (1 ช่อง = 1 บล็อก)

CRITICAL — ใช้ Stage terminology เท่านั้น (Awareness / Consideration / Conversion) ห้ามใช้คำว่า "Layer 1/2/3"
CRITICAL — ถ้า DASHBOARD_CONTEXT ไม่มีข้อมูลช่องใน stage ใด → **ข้าม stage นั้นไปเลย** ห้ามเขียน "ไม่มีข้อมูล" / "no data" / placeholder ใดๆ
CRITICAL — แต่ละช่องประเมินด้วย KPI ที่เหมาะกับ stage ของช่องนั้น (อ้างอิงจาก channel.stage และ stageBreakdown.primaryKpi):
  - Awareness channels (เช่น Facebook, Google Display, TikTok): ประเมินด้วย CPM, CTR — **ไม่ใช่ CPL**
    (ไม่มี Frequency ในระบบนี้ และ Reach มีแค่ยอดรวมทั้ง workspace ไม่มีรายช่อง — ห้ามอ้างสองตัวนี้รายช่อง)
  - Consideration channels (เช่น Google Search, LINE Ads): ประเมินด้วย CTR, CPC
    (คลิกที่มีคือ clicks รวม ไม่มี link clicks แยก และไม่มี landing page views)
  - Conversion channels (เช่น Performance Max, Lead Gen): ประเมินด้วย CPL, CVR, ROAS
    (CPL = cost ÷ channels[].leadConversions ใช้ได้เฉพาะช่องที่ leadConversions ไม่ใช่ null
     ช่องที่เป็น null คือแพลตฟอร์มไม่แยก lead ให้ ต้องรายงานเป็น "ต้นทุนต่อ conversion" ตามชื่อจริง ห้ามเรียกว่า CPL)

สำหรับ TOP 5 channels (เรียงตาม spend), เขียนบล็อกต่อช่อง:

### [Channel Name] · 📢/🔍/🎯 [Stage] — [Verdict: 🟢 Healthy / 🟡 Watch / 🔴 Action Required]

**Performance** (3-5 บรรทัด): ตัวเลขสำคัญของช่องนี้ตาม Primary KPI ของ stage + เปรียบเทียบกับ benchmark/peers
**Diagnosis** (1 ประโยค): สาเหตุหลักของผลงาน อ้างอิงข้อมูลตัวเลข (e.g., "CPM ฿27 ต่ำกว่าช่องอื่นชัดเจน แต่ CVR 0.09% → ได้ความสนใจถูก แต่ไม่แปลงเป็น conversion")
**Recommendation** (1 บรรทัด): action เฉพาะช่องนี้ — Scale / Hold / Reduce / Pause + จำนวนเงิน

ปิดท้ายด้วยตาราง summary 1 ตาราง: Channel | Stage | Spend | Primary KPI Value | Verdict | Next Action

ห้ามวิเคราะห์เกิน 5 ช่อง — เลือกเฉพาะที่มีนัยยะสำคัญ
ห้ามใส่หัวข้อหรือบล็อกของ stage ที่ไม่มีช่องอยู่`,
  badgeColor: 'bg-emerald-100 text-emerald-700',
}

/**
 * 4. Funnel Analysis — awareness → consideration → conversion flow
 */
export const FUNNEL_ANALYSIS_TEMPLATE: ReportTemplate = {
  key: 'funnel_analysis',
  mode: 'performance',
  labelTh: 'วิเคราะห์ Funnel',
  descriptionKey: 'reports.tplFunnelAnalysis',
  icon: 'filter',
  sections: ['funnel_analysis', 'stage_breakdown', 'channel_performance'],
  aiPromptSuffix: 'วิเคราะห์ Funnel — อัตรา Conversion ระหว่างขั้นตอน และจุดที่ควรปรับปรุง',
  aiPromptStructure: `**Funnel Drop-off Analysis** — เดินตาม funnel ขั้นต่อขั้น

funnel นี้มีสองรอยต่อ: impressions → clicks → conversions
มี lead count (channels[].leadConversions) เป็นสับเซตของ conversions ไม่ใช่ขั้นแยกก่อนหน้า —
รายงานเป็นส่วนหนึ่งของ conversions ได้ แต่ห้ามประดิษฐ์เป็นขั้นที่สามใน funnel
และห้ามคำนวณอัตรา lead-to-sale ซึ่งข้อมูลชุดนี้ไม่มี

1. **Awareness → Consideration** (Impressions → Clicks):
   - Drop-off rate %, CTR เฉลี่ย, top 3 ช่องที่มี CTR สูงสุด/ต่ำสุด
   - Bottleneck assessment: creative fatigue? messaging mismatch?

2. **Consideration → Conversion** (Clicks → Conversions):
   - CVR เฉลี่ย (conversions ÷ clicks), ต้นทุนต่อ conversion แยกช่องทาง
   - Bottleneck: landing page friction? form complexity? offer mismatch?
   - ถ้าช่องไหนมี spend แต่ conversion เป็น 0 ให้ระบุว่าเป็นปัญหา tracking ไม่ใช่ผลงานแย่

3. **The Bottleneck Verdict**: ระบุชัดเจนว่ารอยต่อไหนเสียโอกาสมากที่สุด + คำนวณว่า "ถ้าแก้จุดนี้ +X% จะได้ conversions เพิ่มกี่ราย"

4. **2 Optimization Levers** (1 lever ต่อรอยต่อ): action เฉพาะ + ตัวเลขเป้าหมาย

ใช้ตาราง funnel visualization: Stage | Volume | Conv Rate | Drop-off %`,
  badgeColor: 'bg-purple-100 text-purple-700',
}

/**
 * 5. Scenario comparison — the reallocation against holding.
 *
 * This asked for a three-column table of "Current Baseline | Max Revenue
 * Scenario | Max ROI Scenario" and two rows, Risk Level and per-scenario
 * Confidence. None of those five things exists. The engine produces exactly two
 * scenarios, "Efficiency reallocation" and "Hold current allocation", and both
 * carry the SAME spend, projectedRevenue and roi deliberately: no marketing-mix
 * model is fitted, so there is no counterfactual to project a different revenue
 * from. Confidence is one workspace-level coverage figure, not a per-scenario
 * one, and no risk level is computed anywhere.
 *
 * So the old template asked for a table whose three columns would be identical
 * and whose two bottom rows had no source — a report the model could only
 * complete by inventing it. This one asks for the comparison that IS available:
 * what the reallocation moves, why the totals do not move with it, and how much
 * history is behind the recommendation.
 */
export const ROI_COMPARISON_TEMPLATE: ReportTemplate = {
  key: 'roi_comparison',
  mode: 'mmm',
  labelTh: 'เปรียบเทียบแนวทางจัดสรรงบ',
  descriptionKey: 'reports.tplRoiComparison',
  icon: 'trending-up',
  sections: ['roi_comparison', 'confidence_breakdown'],
  aiPromptSuffix: 'เทียบ scenarios[0] กับ scenarios[1] — ต่างกันตรงไหน และตัดสินใจจากอะไร',
  aiPromptStructure: `**เปรียบเทียบแนวทางจัดสรรงบ** — สองทางที่เครื่องมือนี้คำนวณได้จริง

ใช้ชื่อ scenario ตามที่มาใน scenarios[].label เท่านั้น อย่าตั้งชื่อเอง

ตาราง 2 column ตาม scenarios[] แต่ละแถวเป็น metric ที่ context มีจริง:
- spend (฿)
- projectedRevenue (฿)
- roi
- change (สิ่งที่ scenario นั้นเปลี่ยน)

**สำคัญ:** ตัวเลขสามแถวแรกจะ "เท่ากัน" ทั้งสอง column และนั่นถูกต้อง — ยังไม่มี
marketing-mix model ที่ fit แล้ว จึงไม่มี counterfactual ที่จะทำนายรายได้ต่างกันได้
ให้อธิบายเรื่องนี้ตรง ๆ ห้ามเติมตัวเลขต่างเพื่อให้ตารางดูมีความหมาย

จากนั้นเขียน 3 ย่อหน้า:

1. **"การย้ายงบนี้เปลี่ยนอะไร"**: อ่านจาก budgetBreakdown[] — ช่องไหนได้เพิ่ม ช่องไหนถูกลด
   เป็นเงินเท่าไร และเกณฑ์คือ conversions ต่อบาท ไม่ใช่ ROAS

2. **"ทำไมตัวเลขรวมถึงไม่ขยับ"**: เพราะงบรวมเท่าเดิม แค่กระจายใหม่ และเพราะยังไม่มีโมเดล
   ที่ทำนาย lift ได้ — สิ่งที่วัดได้คือประสิทธิภาพปัจจุบันของแต่ละช่อง ไม่ใช่ผลลัพธ์ในอนาคต

3. **"พร้อมตัดสินใจแค่ไหน"**: อ่านจาก mmmStatus — dataDays และ spanDays คือปริมาณ
   ประวัติที่มี, confidence คือ "ข้อมูลครอบคลุมแค่ไหน" ไม่ใช่ความมั่นใจของโมเดล
   ปิดท้ายด้วย metric ที่ควรเฝ้าดูหลังตัดสินใจ`,
  badgeColor: 'bg-cyan-100 text-cyan-700',
}

/**
 * 6. Industry Benchmark — how this brand compares to industry standards
 */
export const INDUSTRY_BENCHMARK_TEMPLATE: ReportTemplate = {
  key: 'industry_benchmark',
  mode: 'performance',
  hidden: true, // Hidden until real industry benchmark data is wired (YDMA-205 feedback)
  labelTh: 'เทียบมาตรฐานอุตสาหกรรม',
  descriptionKey: 'reports.tplIndustryBenchmark',
  icon: 'globe',
  sections: ['industry_benchmark', 'proof_points', 'executive_summary'],
  aiPromptSuffix: 'เทียบเทียบผลงานกับมาตรฐานอุตสาหกรรม — อยู่ในระดับใด มีโอกาสพัฒนาตรงไหน',
  aiPromptStructure: `**Industry Benchmark Report** — เปรียบเทียบ KPI ของเรา vs ค่าเฉลี่ยอุตสาหกรรม

1. **Industry Context**: อ้างอิงจาก DASHBOARD_CONTEXT.industry (ถ้ามี). ระบุ:
   - ชื่ออุตสาหกรรม + CAC range ของอุตสาหกรรม
   - KPIs หลักที่อุตสาหกรรมนี้ให้ความสำคัญ + optimization weights (awareness/consideration/conversion/retention)

2. **Benchmark Comparison**: เทียบเฉพาะตัวที่ industry ส่งค่ามาจริง
   - industry ส่งมาแค่ cacRange (ช่วง CAC) และ avgConversionValue เท่านั้น
     **ไม่มี median และไม่มี top 25% ของ CPL / ROAS / CTR / CVR / CPM สักตัว**
   - เทียบ CPL ของเรากับ cacRange ได้ และเทียบมูลค่าต่อ conversion กับ avgConversionValue ได้
   - ตัวที่ industry ไม่ได้ส่งค่ามา → รายงานค่าของเราอย่างเดียว แล้วบอกว่าไม่มีเกณฑ์อุตสาหกรรมให้เทียบ
     **ห้ามเติมตัวเลข benchmark เอง** ตารางที่มีคอลัมน์ว่างดีกว่าตารางที่เต็มด้วยตัวเลขที่แต่งขึ้น
   - Verdict: 🟢 ดีกว่าเกณฑ์ / 🟡 อยู่ในเกณฑ์ / 🔴 ต่ำกว่าเกณฑ์ — ใช้ได้เฉพาะตัวที่มีเกณฑ์จริง

3. **Strengths** (สิ่งที่เราชนะอุตสาหกรรม): 2-3 ข้อพร้อมตัวเลข

4. **Gaps** (สิ่งที่ต้องไล่ตาม): 2-3 ข้อพร้อมขนาด gap (%) + cost ของการไม่ปิด gap

5. **Proof Points** (ถ้ามี DASHBOARD_CONTEXT.industry.proofPoints): ยกตัวอย่าง case ที่ปิด gap คล้ายกัน

6. **Path to Top 25%**: 3 lever หลักที่จะดัน KPI ไปแตะ top quartile + เวลาที่คาดการณ์

ถ้าไม่มี industry context: บอกตรง ๆ ว่า "ไม่มีข้อมูล industry benchmark — กรุณาเลือก industry ใน Settings"`,
  badgeColor: 'bg-rose-100 text-rose-700',
}

/**
 * 7. Thai NL Q&A — natural language media query report
 * (Answers questions like "เดือนนี้ควรตัดงบช่องไหน")
 */
export const THAI_NL_QA_TEMPLATE: ReportTemplate = {
  key: 'thai_nl_qa',
  mode: 'both',
  hidden: true, // Hidden per YDMA-205 feedback — chat_export covers this use case
  labelTh: 'คำถามธุรกิจ → คำตอบ',
  descriptionKey: 'reports.tplThaiNlQa',
  icon: 'message-circle',
  sections: ['executive_summary', 'budget_reallocation', 'confidence_breakdown'],
  aiPromptSuffix: 'ตอบคำถามธุรกิจภาษาไทยด้วยข้อมูลที่เป็นรูปธรรม — "เดือนนี้ควรตัดงบช่องไหน" ตอบพร้อมเหตุผลและตัวเลข',
  aiPromptStructure: `**Q&A Brief** — รูปแบบ Q-A เน้นตอบให้ตรงคำถามด้วยตัวเลข

ถ้ามี chat history: ดึง 3 คำถามหลักที่ user ถาม → format เป็น Q-A block

แต่ละ Q-A block:
- **❓ คำถาม**: <ข้อความ user ตามจริง>
- **💡 คำตอบสั้น (1 ประโยค)**: คำตอบโดยตรง + ตัวเลขหลัก
- **📊 เหตุผล**: 3-5 บรรทัด พร้อมตัวเลขสนับสนุน
- **✅ Action**: action เดียวที่ user ลงมือได้สัปดาห์นี้

ปิดท้ายด้วย:
- **🔮 คำถามที่ user น่าจะถามต่อ**: 2-3 คำถามแนะนำ + เหตุผลสั้น ๆ ว่าทำไมควรถาม

ห้ามใช้ table — รูปแบบนี้เน้น Q-A อ่านง่าย`,
  badgeColor: 'bg-indigo-100 text-indigo-700',
}

/**
 * 8. Chat Export — synthesize a full chat conversation into structured report
 */
export const CHAT_EXPORT_TEMPLATE: ReportTemplate = {
  key: 'chat_export',
  mode: 'both',
  labelTh: 'สรุปจากแชท',
  descriptionKey: 'reports.tplChatExport',
  icon: 'message-square-quote',
  sections: ['executive_summary', 'stage_breakdown', 'channel_performance', 'funnel_analysis', 'methodology'],
  aiPromptSuffix: 'สังเคราะห์บทสนทนาทั้งหมดเป็นรายงานเชิงปฏิบัติ',
  aiPromptStructure: `**Chat Export Report** — สังเคราะห์บทสนทนาทั้งหมดเป็นรายงานเชิงปฏิบัติ

CRITICAL — ห้ามใช้คำว่า "Layer 1/2/3" หรือ "Layer" ใดๆ ในรายงาน. ใช้คำว่า "Delivery / Traffic / Conversion" (diagnostic) หรือ "Awareness / Consideration / Conversion" (funnel stage) ตามบริบทเท่านั้น เพื่อไม่ให้สับสน
CRITICAL — ถ้าหัวข้อใดไม่มีข้อมูลในบทสนทนา → **ข้ามไปเลย** ห้ามเขียน "ไม่มีข้อมูล" / placeholder

1. **🎯 Conversation Recap** (2-3 บรรทัด): user มาคุยเรื่องอะไร, focus area, ประเด็นที่ใช้เวลาคุยเยอะ

2. **🔬 Diagnostic Findings** — สังเคราะห์สิ่งที่ค้นพบในแชท แบ่งตามหมวด (เขียนเฉพาะหมวดที่มีข้อค้นพบจริง):
   - **Delivery diagnostics**: ข้อสังเกตเกี่ยวกับ CPM และ Impressions (ระบบนี้ไม่มี Frequency และไม่มี Reach)
   - **Traffic diagnostics**: ข้อสังเกตเกี่ยวกับ CTR, CPC, traffic quality
   - **Conversion diagnostics**: ข้อสังเกตเกี่ยวกับ CVR และต้นทุนต่อ conversion (ไม่มีข้อมูล lead quality ในระบบนี้)
   ระบุชัดเจน: **bottleneck อยู่ตรงไหน** (ถ้าแชทพอบอกได้)

3. **🛑 STOP — สิ่งที่ต้อง pause ทันที** (รายการ + เหตุผล + ตัวเลข)

4. **⚙️ ACTION — สิ่งที่ต้องปรับสัปดาห์นี้** (รายการ + วิธีปรับ + เป้าหมาย metric)

5. **📈 SCALE — สิ่งที่ควรเพิ่มงบ** (รายการ + จำนวนเงิน + คาดการณ์ผลลัพธ์)

6. **❓ Open Questions** — คำถามที่ user ยังไม่ได้รับคำตอบครบ + วิธีหาข้อมูลเพิ่ม

ทุก recommendation ต้องอ้าง chat turn ที่เกี่ยวข้อง (e.g., "ตามที่คุยกันเรื่อง Channel X — แนะนำให้...")`,
  badgeColor: 'bg-teal-100 text-teal-700',
}

/**
 * 9. MMM Executive Summary — board-ready MMM summary
 */
export const MMM_EXECUTIVE_SUMMARY_TEMPLATE: ReportTemplate = {
  key: 'mmm_executive_summary',
  mode: 'mmm',
  labelTh: 'สรุปผู้บริหาร (MMM)',
  descriptionKey: 'reports.tplMmmExecutiveSummary',
  icon: 'crown',
  sections: ['executive_summary', 'budget_reallocation', 'confidence_breakdown'],
  aiPromptSuffix: 'เขียนสรุป MMM สำหรับผู้บริหาร — การจัดสรรงบใหม่ + ผลที่คาดการณ์ + ระดับความเชื่อมั่น ไม่เกิน 3 ย่อหน้า',
  aiPromptStructure: `**MMM Executive Summary** — board-ready, 3 ย่อหน้า

1. **The Recommendation** (1 ย่อหน้า): แนะนำให้ทำอะไร — จำนวนเงินที่ย้าย ช่องต้นทาง ช่องปลายทาง
   จาก BUDGET ALLOCATION CHANGES ห้ามใส่ projected revenue uplift: งบรวมเท่าเดิม
   ไม่มีโมเดลที่ทำนายผลหลังย้าย และทั้งสอง scenario ถือตัวเลขเดียวกันโดยตั้งใจ
2. **How much to trust it** (1 ย่อหน้า): data coverage % + จำนวนวันที่มีข้อมูล + บอกตรงๆ ว่ายังไม่มี model ที่ fit ไว้ (ไม่มี R²/MAPE ให้อ้าง) แล้วแปลผลให้คนไม่ใช่ analyst เข้าใจ ("พอใช้ตัดสินใจ" / "ใช้เป็นแนวทาง" / "ต้องรอข้อมูลเพิ่ม")
3. **The Risk & Watch** (1 ย่อหน้า): ความเสี่ยง 1-2 อย่างจากสิ่งที่ข้อมูลบอกได้จริง
   + metric จาก IMPACT TOTALS ที่ควรเฝ้าดู ห้ามตั้ง threshold rollback เป็นตัวเลข

ห้ามใส่ตาราง — ให้บรรยายเป็น narrative สั้นกระชับ`,
  badgeColor: 'bg-amber-100 text-amber-700',
}

/**
 * 10. MMM Data Readiness — how much of the workspace's history is actually there.
 *
 * Was "MMM Confidence Audit", a report built entirely around R-Squared, MAPE and
 * a model-trust verdict. No model is fitted, so the template could only ever
 * instruct the assistant to invent fit statistics and then reason about them.
 */
export const MMM_CONFIDENCE_TEMPLATE: ReportTemplate = {
  key: 'mmm_confidence',
  mode: 'mmm',
  labelTh: 'ตรวจสอบความพร้อมของข้อมูล MMM',
  descriptionKey: 'reports.tplMmmConfidence',
  icon: 'shield-check',
  sections: ['confidence_breakdown', 'methodology'],
  aiPromptSuffix: 'อธิบายว่าข้อมูลพร้อมแค่ไหน — ตัวเลขไหนใช้ตัดสินใจได้ ตัวเลขไหนยังไม่ได้',
  aiPromptStructure: `**MMM Data Readiness** — ผู้ใช้ต้องรู้ว่าตัวเลขบนหน้านี้ใช้ตัดสินใจได้แค่ไหน

สำคัญ: ยังไม่มี marketing-mix model ที่ fit ไว้ ห้ามอ้าง R², MAPE, adstock หรือ
saturation ถ้าไม่มีข้อมูลรองรับข้อไหน ให้บอกว่าไม่มี อย่าประมาณค่าให้

1. **ข้อมูลที่มี** (1 ย่อหน้า): data coverage %, จำนวนวันที่มีข้อมูลเทียบกับช่วงทั้งหมด,
   วันล่าสุดที่ sync — และ verdict ("พอใช้ตัดสินใจ" / "ใช้เป็นแนวทาง" / "ต้องรอข้อมูลเพิ่ม")

2. **อะไรใช้ได้** (bullet list): ตัวเลขที่วัดมาจริง — spend, impressions, clicks,
   conversions, cost per conversion และการจัดอันดับช่องทางตาม conversions ต่อบาท

3. **อะไรยังใช้ไม่ได้** (bullet list): revenue และ ROAS (conversion_value ยังไม่ sync),
   ตัวเลขพยากรณ์ล่วงหน้า, ช่องทางที่มีข้อมูลน้อยกว่าเกณฑ์ และผลเชิงสาเหตุทุกชนิด
   (efficiency reallocation ไม่ได้บอกว่าย้ายงบแล้วจะได้ผลเท่าไร)

4. **ต้องทำอะไรจึงจะดีขึ้น**: อ้างจาก mmmStatus.nextSteps เท่านั้น อย่าคิดข้อใหม่เอง`,
  badgeColor: 'bg-violet-100 text-violet-700',
}

// ============================================================================
// REGISTRY
// ============================================================================

export const REPORT_TEMPLATES: Record<string, ReportTemplate> = {
  executive_summary: EXECUTIVE_SUMMARY_TEMPLATE,
  channel_performance: CHANNEL_PERFORMANCE_TEMPLATE,
  funnel_analysis: FUNNEL_ANALYSIS_TEMPLATE,
  industry_benchmark: INDUSTRY_BENCHMARK_TEMPLATE,
  budget_reallocation: BUDGET_REALLOCATION_TEMPLATE,
  roi_comparison: ROI_COMPARISON_TEMPLATE,
  mmm_executive_summary: MMM_EXECUTIVE_SUMMARY_TEMPLATE,
  mmm_confidence: MMM_CONFIDENCE_TEMPLATE,
  thai_nl_qa: THAI_NL_QA_TEMPLATE,
  chat_export: CHAT_EXPORT_TEMPLATE,
}

/**
 * Default report template (used when none is specified)
 */
export const DEFAULT_REPORT_TEMPLATE = EXECUTIVE_SUMMARY_TEMPLATE

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get a report template by key. Falls back to executive summary.
 */
export function getReportTemplate(key: string): ReportTemplate {
  return REPORT_TEMPLATES[key] ?? DEFAULT_REPORT_TEMPLATE
}

/**
 * Get template options for UI, optionally filtered by chat mode.
 * Pass mode='performance' on /campaigns, mode='mmm' on /mmm so the picker
 * only shows templates that fit the agent's analytical scope.
 * Templates with mode='both' show in either picker.
 */
export function getReportTemplateOptions(
  mode?: ReportMode
): Array<{ value: string; labelTh: string; descriptionKey: string }> {
  const visible = Object.values(REPORT_TEMPLATES).filter((t) => !t.hidden)
  const filtered = mode
    ? visible.filter((t) => t.mode === mode || t.mode === 'both')
    : visible
  return filtered.map((t) => ({
    value: t.key,
    labelTh: t.labelTh,
    descriptionKey: t.descriptionKey,
  }))
}

export default {
  REPORT_TEMPLATES,
  DEFAULT_REPORT_TEMPLATE,
  getReportTemplate,
  getReportTemplateOptions,
}
